// İSG Portal — Araç kaydı form çekmecesi (oluştur / düzenle; api-contract §23.0.2, SP §23.2 / EP §23.1).
// SP ve EP kopyaları GL12'de birleşti; uç ve önbellek portala göre `VehicleClient`'tan gelir.
// Yalnız açıkken monte edilir (çağıran `key` verir) → RHF `defaultValues` her açılışta taze.
// Plaka kilitliyse düzenlenemez (statik metin; salt-okunur input Carbon'da düzenlenebilir görünür) ve
// PUT tam-replace olduğundan mevcut plaka aynen gönderilir. Karar sunucudadır; 409 alan hatasına düşer.
//
// KURAL: `--z-popover` katmanı tokens.css'te tanımlanana kadar bu çekmecenin İÇİNE `RowActionsMenu`,
// `SelectMenu` veya `Tooltip` konmaz — çekmecenin altında kalırlar. Vazgeçme onayı `Modal` olarak kalır.

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { type RefObject, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { hasErrorCode, isApiError } from '../api/errors';
import { resolveApiErrorMessage } from '../i18n/apiError';
import {
  descriptionTooLong,
  displayPlate,
  plateIssue,
  plateIssueMessage,
  vehicleDescriptionTooLongMessage,
  vehicleFieldErrorMessage,
  vehicleFieldErrors,
} from '../permits/vehicleRegistry';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Field } from '../ui/Field';
import { Input } from '../ui/Input';
import { Sheet } from '../ui/Sheet';
import { Textarea } from '../ui/Textarea';
import { useDiscardGuard } from '../ui/useDiscardGuard';
import type { VehicleClient } from './VehicleClient';

export type VehicleFormTarget =
  | { mode: 'create' }
  | {
      mode: 'edit';
      vehicle: { uuid: string; plate: string; description: string | null };
      plateLocked: boolean;
    };

export interface VehicleFormSheetProps {
  client: VehicleClient;
  target: VehicleFormTarget;
  onClose: () => void;
  /** Açan öğenin ref'i — kapanışta odak buraya döner. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}

function isPlateError(error: unknown): boolean {
  return (
    hasErrorCode(error, 'VEHICLE_PLATE_ALREADY_EXISTS') ||
    hasErrorCode(error, 'VEHICLE_PLATE_LOCKED')
  );
}

export function VehicleFormSheet({
  client,
  target,
  onClose,
  restoreFocusRef,
}: VehicleFormSheetProps) {
  const { t } = useTranslation();
  const inflightRef = useRef(false);
  const editing = target.mode === 'edit' ? target : null;
  const plateLocked = editing?.plateLocked ?? false;

  const schema = useMemo(
    () =>
      z.object({
        // Görünen plaka girildiği gibi (yalnız trim); anahtar (harf+rakam) boş olamaz ve ≤ 20 olmalı.
        // Kilitliyse plaka girdisi yok ve mevcut plaka aynen gönderilir → istemci kuralı uygulanmaz.
        plate: plateLocked
          ? z.string()
          : z
              .string()
              .transform(displayPlate)
              .pipe(
                z.string().superRefine((p, ctx) => {
                  const issue = plateIssue(p);
                  if (issue) {
                    ctx.addIssue({ code: 'custom', message: plateIssueMessage(issue, t) });
                  }
                }),
              ),
        description: z
          .string()
          .trim()
          .refine((d) => !descriptionTooLong(d), vehicleDescriptionTooLongMessage(t)),
      }),
    [t, plateLocked],
  );
  type FormValues = z.input<typeof schema>;
  type Parsed = z.output<typeof schema>;

  const { register, handleSubmit, setError, formState } = useForm<FormValues, unknown, Parsed>({
    resolver: zodResolver(schema),
    defaultValues: {
      plate: editing?.vehicle.plate ?? '',
      description: editing?.vehicle.description ?? '',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: Parsed) => {
      const body = {
        // Kilitliyse mevcut plaka AYNEN gönderilir (PUT tam-replace; anahtar değişmez → serbest).
        plate: plateLocked && editing ? editing.vehicle.plate : values.plate,
        description: values.description.length > 0 ? values.description : null,
      };
      return client.saveVehicle(editing?.vehicle.uuid ?? null, body);
    },
    onSuccess: () => {
      client.invalidate();
      onClose();
    },
    onError: (error, values) => {
      // Her hata listeyi tazeler (404 VEHICLE_NOT_FOUND, bayat kilit olgusu, …).
      client.invalidate();
      if (isPlateError(error) && !plateLocked) {
        setError('plate', { type: 'server', message: resolveApiErrorMessage(error, t) });
      }
      for (const item of vehicleFieldErrors(error)) {
        if (item.field === 'plate' && plateLocked) {
          continue; // girdi yok → banner'da kalır
        }
        setError(item.field, {
          type: 'server',
          message: vehicleFieldErrorMessage(item, error, t, values.description),
        });
      }
    },
    onSettled: () => {
      inflightRef.current = false;
    },
  });

  const submitting = mutation.isPending;
  // Render sırasında okunur: RHF `formState` vekili yalnız render'da okunan alana abone olur;
  // `requestClose` içinde ilk kez okunan `isDirty` hep `false` döner ve vazgeç onayı hiç açılmaz.
  const dirty = formState.isDirty;
  const { requestClose, discardDialog } = useDiscardGuard({ dirty, busy: submitting, onClose });

  // Alana bağlanamayan hata banner'a düşer; kilitli plakada girdi olmadığından plaka hatası da.
  const mappedCount = vehicleFieldErrors(mutation.error).filter(
    (item) => !(item.field === 'plate' && plateLocked),
  ).length;
  const totalFieldErrors = isApiError(mutation.error) ? mutation.error.errors.length : 0;
  const plateHandled = isPlateError(mutation.error) && !plateLocked;
  const bannerError =
    mutation.isError && !plateHandled && (mappedCount === 0 || totalFieldErrors > mappedCount);
  const plateError = formState.errors.plate?.message;
  const descriptionError = formState.errors.description?.message;

  return (
    <>
      <Sheet
        open
        onOpenChange={(next) => !next && requestClose()}
        busy={submitting}
        tile
        title={editing ? t('vehicles.form.edit.title') : t('vehicles.add')}
        description={
          editing ? t('vehicles.form.edit.description') : t('vehicles.form.create.description')
        }
        closeLabel={t('common.close')}
        restoreFocusRef={restoreFocusRef}
        footer={
          <>
            <Button variant="secondary" onClick={requestClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="vehicle-form" loading={submitting}>
              {editing ? t('vehicles.form.submit.edit') : t('vehicles.form.submit.create')}
            </Button>
          </>
        }
      >
        <form
          id="vehicle-form"
          className="flex flex-col gap-4"
          noValidate
          onSubmit={handleSubmit((values) => {
            // Eşzamanlı çift Enter: RHF doğrulaması async → senkron ref ile ikinci gönderimi kes.
            if (inflightRef.current || mutation.isPending) {
              return;
            }
            inflightRef.current = true;
            mutation.mutate(values);
          })}
        >
          {bannerError ? (
            <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
          ) : null}

          {plateLocked && editing ? (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted">{t('vehicles.form.field.plate')}</span>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-fg">
                <Lock className="h-4 w-4 text-muted" aria-hidden="true" />
                {editing.vehicle.plate}
              </span>
              <p className="text-xs text-muted">{t('vehicles.form.plateLocked')}</p>
            </div>
          ) : (
            <Field
              htmlFor="vehicle-plate"
              label={t('vehicles.form.field.plate')}
              required
              error={plateError}
            >
              <Input
                id="vehicle-plate"
                autoComplete="off"
                invalid={Boolean(plateError)}
                aria-describedby={plateError ? 'vehicle-plate-error' : undefined}
                placeholder={t('vehicles.form.platePlaceholder')}
                {...register('plate')}
              />
            </Field>
          )}

          <Field
            htmlFor="vehicle-description"
            label={t('vehicles.form.field.description')}
            error={descriptionError}
          >
            <Textarea
              id="vehicle-description"
              rows={3}
              invalid={Boolean(descriptionError)}
              aria-describedby={descriptionError ? 'vehicle-description-error' : undefined}
              {...register('description')}
            />
          </Field>
        </form>
      </Sheet>

      {discardDialog}
    </>
  );
}
